import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, getToken, setToken } from '@/lib/api';
import { toast } from 'sonner';
import { getDemoCode, setDemoCode } from '@/lib/demoAccess';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [practice, setPractice] = useState(null);
  const [packetTypes, setPacketTypes] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setPractice(null);
      setAuthLoading(false);
      return;
    }
    try {
      const r = await api.get('/auth/me');
      setUser(r.data.user);
      setPractice(r.data.practice);
      setPacketTypes(r.data.packet_types || []);
    } catch (e) {
      setUser(null);
      setPractice(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  useEffect(() => {
    const handler = () => { setUser(null); setPractice(null); };
    window.addEventListener('auth-expired', handler);
    return () => window.removeEventListener('auth-expired', handler);
  }, []);

  const applyAuth = useCallback(async (data) => {
    setToken(data.token);
    setUser(data.user);
    setPractice(data.practice);
    try {
      const r = await api.get('/packet-types');
      setPacketTypes(r.data || []);
    } catch (e) { /* non-fatal */ }
    setAuthLoading(false);
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post('/auth/login', { email, password });
    if (r.data?.mfa_required) {
      return { mfaRequired: true, mfaToken: r.data.mfa_token };
    }
    const user = await applyAuth(r.data);
    return { user };
  }, [applyAuth]);

  const verifyMfa = useCallback(async (mfaToken, code) => {
    const r = await api.post('/auth/mfa/verify', { mfa_token: mfaToken, code });
    const user = await applyAuth(r.data);
    return {
      user,
      backupCodeUsed: r.data.backup_code_used,
      backupCodesRemaining: r.data.backup_codes_remaining,
    };
  }, [applyAuth]);

  const signup = useCallback(async (payload) => {
    const r = await api.post('/auth/signup', payload);
    return applyAuth(r.data);
  }, [applyAuth]);

  // `code` is only passed when the user has just typed it; every other caller
  // (role switcher, second demo button) reuses the code already unlocked this
  // session. Stored only after the API accepts it.
  const demoLogin = useCallback(async (role, code) => {
    const accessCode = code ?? getDemoCode();
    const r = await api.post(`/auth/demo/${role}`, { access_code: accessCode });
    setDemoCode(accessCode);
    return applyAuth(r.data);
  }, [applyAuth]);

  const acceptInvite = useCallback(async (inviteToken, payload) => {
    const r = await api.post(`/invites/token/${inviteToken}/accept`, payload);
    return applyAuth(r.data);
  }, [applyAuth]);

  const logout = useCallback(() => {
    setToken('');
    setUser(null);
    setPractice(null);
  }, []);

  // Idle-session timeout: the practice setting (Admin Settings), 30 min when
  // unset (owner has no practice). Any input resets the clock; tabs share it
  // via localStorage so activity in one tab keeps the others signed in.
  const idleMinutes = Number(practice?.settings?.session_timeout_minutes) || 30;
  useEffect(() => {
    if (!user) return undefined;
    const KEY = 'fmla_last_activity_v1';
    const touch = () => { try { localStorage.setItem(KEY, String(Date.now())); } catch (e) { /* private mode */ } };
    touch();
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((ev) => window.addEventListener(ev, touch, { passive: true }));
    const timer = setInterval(() => {
      let last = Date.now();
      try { last = Number(localStorage.getItem(KEY)) || last; } catch (e) { /* private mode */ }
      if (Date.now() - last > idleMinutes * 60000) {
        logout();
        toast.info('Signed out after inactivity');
      }
    }, 30000);
    return () => {
      clearInterval(timer);
      events.forEach((ev) => window.removeEventListener(ev, touch));
    };
  }, [user, idleMinutes, logout]);

  const role = user?.role || '';

  return (
    <AppContext.Provider value={{
      user, practice, packetTypes, role, authLoading,
      login, verifyMfa, signup, demoLogin, acceptInvite, logout,
      refreshUser, setPractice,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
